/**
 * Figma Variables from DIDIM_APP design
 * Generated from: https://www.figma.com/design/VS3wArerPx7MLIHOZtvf23/DIDIM_APP
 *
 * This file contains all design tokens including:
 * - Color schemes (Light/Dark mode)
 * - Typography system
 * - Spacing & Layout
 * - Component sizes
 * - Shadows & Effects
 */

// Color System - Light Mode
export const lightColors = {
    // Primary Colors
    primary: {
        50: '#fff5f5',
        100: '#fed7d7',
        200: '#feb2b2',
        300: '#fc8181',
        400: '#f56565',
        500: '#e53e3e',
        600: '#c53030',
        700: '#a23d3d',
        800: '#822727',
        900: '#63171b',
        main: '#E73829', // DIDIM Brand Red
    },

    // Secondary Colors
    secondary: {
        50: '#f7fafc',
        100: '#edf2f7',
        200: '#e2e8f0',
        300: '#cbd5e0',
        400: '#a0aec0',
        500: '#718096',
        600: '#4a5568',
        700: '#2d3748',
        800: '#1a202c',
        900: '#171923',
        main: '#040116', // DIDIM Brand Dark Blue
    },

    // Neutral Colors
    neutral: {
        white: '#ffffff',
        gray: {
            50: '#f9fafb',
            100: '#f3f4f6',
            200: '#e5e7eb',
            300: '#d1d5db',
            400: '#9ca3af',
            500: '#6b7280',
            600: '#4b5563',
            700: '#374151',
            800: '#1f2937',
            900: '#111827',
        },
        black: '#000000',
    },

    // Semantic Colors
    semantic: {
        success: {
            light: '#4ade80',
            main: '#22c55e',
            dark: '#16a34a',
        },
        warning: {
            light: '#fbbf24',
            main: '#f59e0b',
            dark: '#d97706',
        },
        error: {
            light: '#f87171',
            main: '#ef4444',
            dark: '#dc2626',
        },
        info: {
            light: '#60a5fa',
            main: '#3b82f6',
            dark: '#2563eb',
        },
    },

    // Background Colors
    background: {
        default: '#F5F5FA',
        // paper: '#ffffff',
        surface: '#f8fafc',
        main: '#F5F5FA',
        sub: '#FFFFFF',
    },

    // Text Colors
    text: {
        primary: '#161B39',
        secondary: '#6b7280',
        disabled: '#9ca3af',
        inverse: '#ffffff',
        main: '#161B39',
    },

    // Figma Specific Colors - Light Mode
    figma: {
        // Base Colors
        black: '#000000',
        icon: '#9B96A2',
        mainBg: '#F5F5FA',
        Red_didim: '#FF4E02',

        // White Variations with Opacity
        white: {
            100: '#000000', // Inverted for light mode
            80: 'rgba(0, 0, 0, 0.80)', // 80% opacity
            60: 'rgba(0, 0, 0, 0.60)', // 50% opacity (adjusted for light mode)
            40: ' rgba(0, 0, 0, 0.40)', // 30% opacity (adjusted for light mode)
            30: 'rgba(0, 0, 0, 0.20)', // 20% opacity (adjusted for light mode)
            20: 'rgba(0, 0, 0, 0.10)', // 10% opacity (adjusted for light mode)
            10: 'rgba(0, 0, 0, 0.05)', // 5% opacity for light mode
            5: 'rgba(0, 0, 0, 0.03)', // 3% opacity for light mode
        },

        // UI Colors
        buttonText: '#FFFFFF',
        iconWhite: '#FFFFFF',
        buttonOrange: '#FF6C02',
        block: '#FFFFFF',
        bottomColor: '#FFFFFF',
        popupBG: '#FFF',

        // Challenge & Activity Colors
        steps: '#9AD700',
        stepsDark: '#DCF1B3',
        didim: '#FFA600',
        calorieRed: '#FF2F00',
        time: '#22BDFF',
        upRed: '#FF0000',
        downBlue: '#0F93FF',
        heartRate: '#FF00A6',
        heartRateDark: '#FFCCED',
        walkingChallengeBg: '#60BA0B',
        heartRateChallengeBg: '#91279A',

        // Tournament & Ranking Colors
        champTournament: '#014EFF',
        champTournamentFinish: '#BCC5DD',
        champTournamentHighlight: '#00D8EF',
        yellowText: '#FFF307',
        champRanking: '#6B00FE',
        champRankingFinish: '#473A59',
        champRankingHighlight: '#D8C3FF',
    },
};

// Color System - Dark Mode
export const darkColors = {
    // Primary Colors (same as light for brand consistency)
    primary: lightColors.primary,
    secondary: lightColors.secondary,

    // Neutral Colors
    neutral: {
        white: '#ffffff',
        gray: {
            50: '#111827',
            100: '#1f2937',
            200: '#374151',
            300: '#4b5563',
            400: '#6b7280',
            500: '#9ca3af',
            600: '#d1d5db',
            700: '#e5e7eb',
            800: '#f3f4f6',
            900: '#f9fafb',
        },
        black: '#000000',
    },

    // Semantic Colors (adjusted for dark mode)
    semantic: {
        success: {
            light: '#16a34a',
            main: '#22c55e',
            dark: '#4ade80',
        },
        warning: {
            light: '#d97706',
            main: '#f59e0b',
            dark: '#fbbf24',
        },
        error: {
            light: '#dc2626',
            main: '#ef4444',
            dark: '#f87171',
        },
        info: {
            light: '#2563eb',
            main: '#3b82f6',
            dark: '#60a5fa',
        },
    },

    // Background Colors
    background: {
        default: '#0f172a',
        // paper: '#1e293b',
        surface: '#334155',
        main: '#040116',
        sub: '#040116',
    },

    // Text Colors
    text: {
        primary: '#f8fafc',
        secondary: '#cbd5e0',
        disabled: '#64748b',
        inverse: '#1f2937',
        main: '#E4E6EE',
    },

    // Figma Specific Colors - Dark Mode
    figma: {
        // Base Colors
        black: '#000000',
        icon: '#9B96A2',
        mainBg: '#040116',
        Red_didim: '#E73829',

        // White Variations with Opacity
        white: {
            100: '#FFFFFF',
            80: 'rgba(255, 255, 255, 0.80)', // 80% opacity
            60: 'rgba(255, 255, 255, 0.60)', // 60% opacity
            40: 'rgba(255, 255, 255, 0.40)', // 40% opacity
            30: 'rgba(255, 255, 255, 0.30)', // 30% opacity
            20: 'rgba(255, 255, 255, 0.20)', // 20% opacity
            10: 'rgba(255, 255, 255, 0.10)', // 10% opacity for dark mode
            5: 'rgba(255, 255, 255, 0.05)', // 5% opacity
        },

        // UI Colors
        buttonText: '#FFFFFF',
        iconWhite: '#FFFFFF',
        buttonOrange: '#FF6105',
        block: 'rgba(234, 232, 243, 0.20)', // 20% opacity
        bottomColor: '#0084FF',
        popupBG: '#2F2E31',

        // Challenge & Activity Colors
        steps: '#9AD700',
        stepsDark: '#535B35',
        didim: '#FFAA00',
        calorieRed: '#FF2F00',
        time: '#22BDFF',
        upRed: '#FF0000',
        downBlue: '#0F93FF',
        heartRate: '#FF00A6',
        heartRateDark: '#6D2F67',
        walkingChallengeBg: '#539F0B',
        heartRateChallengeBg: '#91279A',

        // Tournament & Ranking Colors
        champTournament: '#014EFF',
        champTournamentFinish: '#304169',
        champTournamentHighlight: '#00D8EF',
        yellowText: '#FFF307',
        champRanking: '#6B00FE',
        champRankingFinish: '#473A59',
        champRankingHighlight: '#D8C3FF',
    },
};

// Typography System
export const typography = {
    // Font Families
    fontFamily: {
        primary: [
            'Pretendard JP',
            'Pretendard',
            '-apple-system',
            'BlinkMacSystemFont',
            'system-ui',
            'Roboto',
            '"Helvetica Neue"',
            '"Segoe UI"',
            '"Apple SD Gothic Neo"',
            '"Noto Sans KR"',
            '"Malgun Gothic"',
            'sans-serif',
        ].join(','),
        secondary: ['Inter', 'system-ui', 'sans-serif'].join(','),
        mono: [
            'JetBrains Mono',
            'Fira Code',
            'Consolas',
            'Monaco',
            'monospace',
        ].join(','),
    },

    // Font Sizes
    fontSize: {
        xs: '0.75rem', // 12px
        sm: '0.875rem', // 14px
        base: '1rem', // 16px
        lg: '1.125rem', // 18px
        xl: '1.25rem', // 20px
        '2xl': '1.5rem', // 24px
        '3xl': '1.875rem', // 30px
        '4xl': '2.25rem', // 36px
        '5xl': '3rem', // 48px
        '6xl': '3.75rem', // 60px
    },

    // Font Weights
    fontWeight: {
        thin: 100,
        extralight: 200,
        light: 300,
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
        extrabold: 800,
        black: 900,
    },

    // Line Heights
    lineHeight: {
        none: 1,
        tight: 1.25,
        snug: 1.375,
        normal: 1.5,
        relaxed: 1.625,
        loose: 2,
    },

    // Letter Spacing
    letterSpacing: {
        tighter: '-0.05em',
        tight: '-0.025em',
        normal: '0em',
        wide: '0.025em',
        wider: '0.05em',
        widest: '0.1em',
    },

    // Typography Variants
    variants: {
        // DIDIM Brand Typography
        digitalPlayground: {
            fontSize: '1rem', // 16px
            fontWeight: 600,
            letterSpacing: '0.025em',
            lineHeight: 1.5,
        },
        didimLogo: {
            fontSize: '3rem', // 48px
            fontWeight: 700,
            letterSpacing: '0.05em',
            lineHeight: 1.2,
        },

        // Component Typography
        h1: {
            fontSize: '2.25rem', // 36px
            fontWeight: 700,
            lineHeight: 1.2,
        },
        h2: {
            fontSize: '1.875rem', // 30px
            fontWeight: 600,
            lineHeight: 1.3,
        },
        h3: {
            fontSize: '1.5rem', // 24px
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h4: {
            fontSize: '1.25rem', // 20px
            fontWeight: 500,
            lineHeight: 1.4,
        },
        h5: {
            fontSize: '1.125rem', // 18px
            fontWeight: 500,
            lineHeight: 1.4,
        },
        h6: {
            fontSize: '1rem', // 16px
            fontWeight: 500,
            lineHeight: 1.4,
        },
        body1: {
            fontSize: '1rem', // 16px
            fontWeight: 400,
            lineHeight: 1.5,
        },
        body2: {
            fontSize: '0.875rem', // 14px
            fontWeight: 400,
            lineHeight: 1.5,
        },
        caption: {
            fontSize: '0.75rem', // 12px
            fontWeight: 400,
            lineHeight: 1.4,
        },
        button: {
            fontSize: '0.875rem', // 14px
            fontWeight: 500,
            lineHeight: 1.5,
            textTransform: 'none',
        },
    },
};

// Spacing System
export const spacing = {
    // Base spacing units
    base: 4, // 4px

    // Spacing scale
    0: '0px',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    7: '28px',
    8: '32px',
    9: '36px',
    10: '40px',
    12: '48px',
    14: '56px',
    16: '64px',
    20: '80px',
    24: '96px',
    28: '112px',
    32: '128px',
    36: '144px',
    40: '160px',
    44: '176px',
    48: '192px',
    52: '208px',
    56: '224px',
    60: '240px',
    64: '256px',
    72: '288px',
    80: '320px',
    96: '384px',

    // Component-specific spacing
    contentMargin: 20,
    logoGap: 10,
    sectionPadding: 32,
    cardPadding: 24,
    buttonPadding: 16,
};

// Component Sizes
export const sizes = {
    // DIDIM Brand Components
    didimLogo: {
        width: 260,
        height: 31.369,
    },
    digitalPlayground: {
        width: 132,
        height: 6,
    },

    // Common Component Sizes
    button: {
        small: { height: 32, padding: '8px 16px' },
        medium: { height: 40, padding: '12px 24px' },
        large: { height: 48, padding: '16px 32px' },
    },
    input: {
        small: { height: 32, padding: '8px 12px' },
        medium: { height: 40, padding: '12px 16px' },
        large: { height: 48, padding: '16px 20px' },
    },
    avatar: {
        small: 32,
        medium: 40,
        large: 48,
        xlarge: 64,
    },
    icon: {
        small: 16,
        medium: 24,
        large: 32,
        xlarge: 48,
    },
};

// Shadows & Effects
export const shadows = {
    // Elevation shadows
    elevation: {
        0: 'none',
        1: '0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)',
        2: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
        3: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
        4: '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
        5: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },

    // Custom shadows
    custom: {
        card: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        button: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        modal: '0px 20px 40px rgba(0, 0, 0, 0.15)',
        tooltip: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    },
};

// Border Radius
export const borderRadius = {
    none: '0px',
    sm: '2px',
    base: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    '2xl': '16px',
    '3xl': '24px',
    full: '9999px',
};

// Z-Index Scale
export const zIndex = {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
};

// Breakpoints
export const breakpoints = {
    xs: '0px',
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1536px',
};

// Transitions
export const transitions = {
    duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
    },
    easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
};

// Main export object
export const figmaVariables = {
    colors: {
        light: lightColors,
        dark: darkColors,
    },
    typography,
    spacing,
    sizes,
    shadows,
    borderRadius,
    zIndex,
    breakpoints,
    transitions,
};

// Individual exports for convenience - removed duplicate exports

// Default export
export default figmaVariables;
